export type Company = {
	id: string
	name?: string | null
	legalForm?: string | null
	managingDirector?: string | null
	street?: string | null
	houseNumber?: string | null
	zipCode?: string | null
	city?: string | null
	email?: string | null
	phone?: string | null
	website?: string | null
	tradeRegisterNumber?: string | null
	ustId?: string | null
	taxId?: string | null
	// Nur für root/Admins geladen; accountId etwa das Stripe-Konto „acct_…“.
	paymentMethods?: { paymentMethod: string; active: boolean; accountId?: string | null }[] | null
}

export type EditableCompanyField =
	| 'name'
	| 'legalForm'
	| 'managingDirector'
	| 'street'
	| 'houseNumber'
	| 'zipCode'
	| 'city'
	| 'email'
	| 'phone'
	| 'website'
	| 'tradeRegisterNumber'
	| 'ustId'
	| 'taxId'

// Die Werte entsprechen exakt den Schreibweisen, die api/src/utils/stripeOnboarding.ts auf einen
// Stripe business_type abbilden kann. Beschränkt auf die Rechtsformen, die in der Gastronomie
// vorkommen — die API akzeptiert weiterhin auch die übrigen.
export type LegalFormOption = {
	label: string
	value: string
	// Einzelunternehmen und GbR stehen in keinem Register.
	hasRegister: boolean
	representativeLabel: string
}

export const LEGAL_FORM_ITEMS: LegalFormOption[] = [
	{
		label: 'Einzelunternehmen',
		value: 'Einzelunternehmen',
		hasRegister: false,
		representativeLabel: 'Inhaber (Vor- und Nachname)',
	},
	{
		label: 'GbR',
		value: 'GbR',
		hasRegister: false,
		representativeLabel: 'Vertretungsberechtigter Gesellschafter',
	},
	{ label: 'e.K.', value: 'e.K.', hasRegister: true, representativeLabel: 'Inhaber' },
	{
		label: 'UG (haftungsbeschränkt)',
		value: 'UG (haftungsbeschränkt)',
		hasRegister: true,
		representativeLabel: 'Geschäftsführer',
	},
	{ label: 'GmbH', value: 'GmbH', hasRegister: true, representativeLabel: 'Geschäftsführer' },
	{
		label: 'GmbH & Co. KG',
		value: 'GmbH & Co. KG',
		hasRegister: true,
		representativeLabel: 'Geschäftsführer der Komplementär-GmbH',
	},
]

export const findLegalForm = (value?: string | null): LegalFormOption | null =>
	LEGAL_FORM_ITEMS.find((item) => item.value === value) ?? null

const ADDRESS_HINT = [
	'Firmenanschrift im Vertrags-Parteienblock',
	'Kolophon der App',
	'Adresse am Stripe-Connect-Account',
]

// Label und Erklärung jedes Firmenfeldes stehen nur hier — die Basisdaten-Tabelle und das
// Formular im Onboarding-Guide lesen beide aus dieser Liste, damit sie nicht auseinanderlaufen.
export const COMPANY_FIELDS: {
	key: EditableCompanyField
	label: string
	hint: string[]
	editable?: boolean
}[] = [
	{
		key: 'legalForm',
		label: 'Rechtsform',
		hint: [
			'Stripe „business_type“: Einzelunternehmen, e.K. und Freiberufler → „individual“, sonst „company“',
			'Fehlt sie, bricht das Stripe-Onboarding ab',
			'Parteienblock und Kolophon, hinter dem Firmennamen',
		],
		editable: true,
	},
	{
		key: 'name',
		label: 'Firmenname',
		hint: [
			'Vertragspartner im Parteienblock',
			'Kolophon der App',
			'Stripe „business_profile.name“, bei juristischen Personen auch „company.name“',
		],
		editable: true,
	},
	{
		key: 'managingDirector',
		label: 'Geschäftsführer / Inhaber',
		hint: [
			'Parteienblock',
			'Kolophon der App als „Gesetzlicher Vertreter“',
			'Unterzeichnername unter den Unterschriftsfeldern',
			'Stripe „individual.first_name“ / „individual.last_name“, am ersten Leerzeichen geteilt',
		],
		editable: true,
	},
	{ key: 'street', label: 'Straße', hint: ADDRESS_HINT, editable: true },
	{ key: 'houseNumber', label: 'Hausnummer', hint: ADDRESS_HINT, editable: true },
	{ key: 'zipCode', label: 'PLZ', hint: ADDRESS_HINT, editable: true },
	{ key: 'city', label: 'Stadt', hint: ADDRESS_HINT, editable: true },
	{
		key: 'email',
		label: 'E-Mail',
		hint: [
			'Stripe „email“ am Connect-Account',
			'Pflicht für das Stripe-Onboarding',
		],
		editable: true,
	},
	{
		key: 'phone',
		label: 'Telefon',
		hint: [
			'Stripe „phone“',
			'Nur deutsche Nummern — Schreibweise egal: „0151 23456789“, „+49 151 …“, „0049 151 …“',
		],
		editable: true,
	},
	{
		key: 'website',
		label: 'Website',
		hint: ['Stripe „business_profile.url“'],
		editable: true,
	},
	{
		key: 'tradeRegisterNumber',
		label: 'Handelsregisternr.',
		hint: [
			'Parteienblock',
			'Kolophon der App',
			'Stripe „company.tax_id“ bei juristischen Personen, nur im Format „HRB 12345“',
		],
		editable: true,
	},
	{
		key: 'ustId',
		label: 'USt-IdNr.',
		hint: [
			'Pflicht für die Aktivierung, alternativ die Steuernr.',
			'Parteienblock, Kundenrechnung, Kolophon der App',
			'Stripe „company.vat_id“ bei juristischen Personen',
		],
		editable: true,
	},
	{
		key: 'taxId',
		label: 'Steuernr.',
		hint: [
			'Pflicht für die Aktivierung, alternativ die USt-IdNr.',
			'Parteienblock und Kundenrechnung',
			'Geht nicht an Stripe',
		],
		editable: true,
	},
]

export const PAYMENT_METHOD_HINT = [
	'Eintrag aus company_payment_method; die aktive Zahlungsart bestimmt, wie Kunden zahlen',
	'„STRIPE“ / „STRIPE_TEST“ = online mit Live- bzw. Test-Account aus der Account-ID',
	'„DOOR“ / „DOOR_TEST“ = an der Tür, Servicegebühr an Beyer Development live bzw. Sandbox',
	'Trägt der Stripe-Schritt selbst ein',
]

export const EDITABLE_COMPANY_FIELDS = COMPANY_FIELDS.filter((field) => field.editable)
